import React, { useState, useRef } from 'react';
import api from '../../services/api';
import { DocumentViewer, PdfThumbnail } from './DocumentViewer';

/* ─────────────────────────────────────────────────────────────────────────── */
/* Formulaire de prêt de véhicule de courtoisie — composant partagé, réutilisé */
/* par la page Véhicules Courtoisie et par l'onglet Courtoisie de la modale     */
/* d'intervention (Planning Atelier). Extrait de VehiculesCourtoisie.js pour    */
/* éviter la duplication entre les deux points d'usage.                        */
/* ─────────────────────────────────────────────────────────────────────────── */

export const CARBURANT_LABEL = { essence: 'Essence', diesel: 'Diesel', electrique: 'Électrique', hybride: 'Hybride' };

export const initPret = {
  vehicule_id: '', client_nom: '', client_prenom: '', client_telephone: '', client_email: '',
  client_date_naissance: '', client_lieu_naissance: '', client_permis_numero: '', client_permis_date_obtention: '',
  date_debut: new Date().toISOString().split('T')[0], date_fin_prevue: '', motif: 'Révision', immat_client: '',
  or_numero: '', km_depart: '', caution_type: 'cheque', caution_montant: '', caution_cheque_numero: '',
  cout_journalier: '', societe: '', autorise_par: '', num_bon_commande: '',
  loueur_id: '', loueur_immat: '', loueur_marque: '', loueur_num_contrat: '',
};

const TAUX_TVA = 8.5; // Martinique
// Récap facturation côté UI (HT / TVA 8,5 % / TTC) ; jours = (fin − début), min 1.
export const calcFacturationUI = (cout, dateDebut, dateFin) => {
  const cj = Number(cout);
  if (!cj || !dateDebut || !dateFin) return null;
  let jours = Math.ceil((new Date(dateFin) - new Date(dateDebut)) / 86400000);
  if (!Number.isFinite(jours) || jours < 1) jours = 1;
  const ht = Math.round(jours * cj * 100) / 100;
  const tva = Math.round(ht * TAUX_TVA) / 100;
  return { jours, ht, tva, ttc: Math.round((ht + tva) * 100) / 100 };
};

// Pièces jointes bufferisées (modale/formulaire Nouveau prêt) — avant la
// création, le prêt n'a pas encore d'id : on garde les fichiers en mémoire
// (aperçu via objectURL, même forme que `fichiers` pour CourtoisieDocsGrid) et
// on les téléverse juste après l'enregistrement, dans handleSavePret.
export function usePretFichiersBuffer() {
  const [pretFichiersBuffer, setPretFichiersBuffer] = useState([]);
  const bufferIdRef = useRef(0);

  const addPretFichierBuffer = (_pretId, file) => {
    const url = URL.createObjectURL(file);
    const id = `tmp-${bufferIdRef.current++}`;
    setPretFichiersBuffer(b => [...b, { id, nom: file.name, type_mime: file.type, url, file }]);
  };
  const removePretFichierBuffer = (_pretId, id) => {
    setPretFichiersBuffer(b => {
      const f = b.find(x => x.id === id);
      if (f) URL.revokeObjectURL(f.url);
      return b.filter(x => x.id !== id);
    });
  };
  const clearPretFichiersBuffer = () => {
    setPretFichiersBuffer(b => { b.forEach(f => URL.revokeObjectURL(f.url)); return []; });
  };

  return { pretFichiersBuffer, addPretFichierBuffer, removePretFichierBuffer, clearPretFichiersBuffer };
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Pièces jointes d'un prêt — même UX que Litiges : vignette / aperçu du        */
/* document + zone glisser-déposer. Réutilise DocumentViewer + PdfThumbnail et   */
/* les classes CSS partagées (shared/DocumentViewer.css).                        */
/* ─────────────────────────────────────────────────────────────────────────── */
export function CourtoisieDocsGrid({ fichiers, pretId, onUpload, onDelete, uploading, canEdit = true }) {
  const [dragging, setDragging] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const inputRef = useRef(null);

  const addFiles = (files) => {
    Array.from(files || []).forEach((f) => onUpload(pretId, f));
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (canEdit) addFiles(e.dataTransfer.files);
  };

  return (
    <div className="documents-grid">
      {fichiers.map((f) => {
        const mime    = f.type_mime || '';
        const isImage = mime.startsWith('image/');
        const isPdf   = mime === 'application/pdf';
        const doc     = { id: f.id, name: f.nom, type: mime, url: f.url };
        return (
          <div key={f.id} className="doc-thumbnail" title={f.nom}>
            {canEdit && (
              <button
                className="doc-remove-btn"
                title="Supprimer ce document"
                onClick={(e) => { e.stopPropagation(); setToDelete(f); }}
              >
                ✕
              </button>
            )}
            <div className="doc-preview" onClick={() => setLightbox(doc)}>
              {isImage ? (
                <img src={f.url} alt={f.nom} />
              ) : isPdf ? (
                <PdfThumbnail url={f.url} />
              ) : (
                <div className="doc-icon">
                  <span className="doc-icon-emoji">📎</span>
                  <span className="doc-icon-label">DOC</span>
                </div>
              )}
            </div>
            <div className="doc-name" title={f.nom}>{f.nom}</div>
          </div>
        );
      })}

      {canEdit && (
        <div
          className={`drop-zone${dragging ? ' dragging' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          title="Glisser-déposer ou cliquer pour ajouter un document"
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            style={{ display: 'none' }}
            disabled={uploading}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
          <div className="drop-zone-content">
            <span className="drop-icon">{uploading ? '⏳' : '📤'}</span>
            <span>{uploading ? 'Envoi…' : <>Glisser-déposer<br />ou cliquer</>}</span>
          </div>
        </div>
      )}

      {!canEdit && fichiers.length === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>Aucun document.</p>
      )}

      {lightbox && <DocumentViewer doc={lightbox} onClose={() => setLightbox(null)} />}

      {toDelete && (
        <div className="doc-confirm-overlay" onClick={() => setToDelete(null)}>
          <div className="doc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p className="doc-confirm-msg">Supprimer le document&nbsp;?</p>
            <p className="doc-confirm-name">« {toDelete.nom} »</p>
            <div className="doc-confirm-actions">
              <button className="btn-secondary" onClick={() => setToDelete(null)}>Annuler</button>
              <button
                className="btn-danger"
                onClick={() => { onDelete(pretId, toDelete.id); setToDelete(null); }}
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Permis de conduire — dépôt + reconnaissance automatique IA (Mistral AI, UE)  */
/* Réutilise les mêmes classes drop-zone/drop-zone-content/drop-icon que        */
/* CourtoisieDocsGrid ci-dessus pour une cohérence visuelle stricte.            */
/* ─────────────────────────────────────────────────────────────────────────── */
export function PermisDropzone({ disabled, onFilePicked, onExtracted }) {
  const [state, setState] = useState('idle'); // idle | analyzing | done | error
  const [preview, setPreview] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const isPdf = file.type === 'application/pdf';
    setPreview({ url: URL.createObjectURL(file), nom: file.name, isPdf });
    onFilePicked(file); // ajoute au buffer pièces jointes existant (même flux que les autres documents)

    if (!file.type.startsWith('image/') && !isPdf) {
      setState('error');
      setErrorMsg("Analyse IA disponible uniquement pour une image (JPG/PNG) ou un PDF du permis — document tout de même ajouté aux pièces jointes.");
      return;
    }
    setState('analyzing');
    setErrorMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/courtoisie/permis/analyser', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onExtracted(res.data.champs || {});
      setState('done');
    } catch (err) {
      setState('error');
      setErrorMsg(err.response?.data?.error || "Échec de l'analyse IA — vous pouvez saisir les champs manuellement.");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled) handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="form-section">
      <h3>🪪 Permis de conduire du client</h3>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>
        Glissez-déposez une photo du permis : les champs ci-dessous seront pré-remplis automatiquement (à vérifier avant enregistrement).
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div
          className={`drop-zone${dragging ? ' dragging' : ''}`}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          title="Glisser-déposer ou cliquer pour ajouter le permis"
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*,.pdf"
            style={{ display: 'none' }}
            disabled={disabled}
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          />
          {preview ? (
            preview.isPdf
              ? <PdfThumbnail url={preview.url} />
              : <img src={preview.url} alt="Permis" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
          ) : (
            <div className="drop-zone-content">
              <span className="drop-icon">{state === 'analyzing' ? '⏳' : '🪪'}</span>
              <span>Glisser-déposer<br />ou cliquer</span>
            </div>
          )}
        </div>
        <div style={{ fontSize: 12, flex: 1 }}>
          {state === 'analyzing' && <span style={{ color: 'var(--text-secondary)' }}>⏳ Analyse IA en cours…</span>}
          {state === 'done' && <span style={{ color: 'var(--color-success)' }}>✅ Champs pré-remplis — vérifiez avant d'enregistrer.</span>}
          {state === 'error' && <span style={{ color: 'var(--color-error)' }}>⚠️ {errorMsg}</span>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Formulaire de prêt — composant pur (props in, setFormPret pour les changements) */
/* Ne gère ni la sauvegarde (POST) ni le wrapper modal-header/footer : ceux-ci   */
/* diffèrent entre les deux points d'usage (VehiculesCourtoisie, InterventionModal). */
/* ─────────────────────────────────────────────────────────────────────────── */
export function PretCourtoisieForm({
  pretMode, formPret, setFormPret, saving,
  vehiculesActifs, loueurs, onOpenAjouterLoueur,
  pretFichiersBuffer, addPretFichierBuffer, removePretFichierBuffer,
}) {
  const isLocation = pretMode === 'location';
  const disponibles = vehiculesActifs.filter(v => v.statut === 'disponible' && !v.bloque);
  const selectedVeh = vehiculesActifs.find(v => String(v.id) === String(formPret.vehicule_id));
  const fact = calcFacturationUI(formPret.cout_journalier, formPret.date_debut, formPret.date_fin_prevue);
  const setP = (patch) => setFormPret(f => ({ ...f, ...patch }));
  const fmtE = (n) => Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <>
      {!isLocation ? (
        <div className="form-section">
          <h3>Véhicule prêté (flotte)</h3>
          <select value={formPret.vehicule_id} onChange={e => setP({ vehicule_id: e.target.value })}>
            <option value="">— Sélectionner un véhicule disponible —</option>
            {disponibles.map(v => (
              <option key={v.id} value={v.id}>
                {v.immatriculation} — {v.marque} {v.modele} ({CARBURANT_LABEL[v.carburant] || v.carburant} · {v.boite_vitesse})
                {v.concession_nom ? ` · ${v.concession_nom}` : ''}
              </option>
            ))}
          </select>
          {selectedVeh && selectedVeh.dernier_km_connu != null && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
              Dernier km connu : <strong>{Number(selectedVeh.dernier_km_connu).toLocaleString('fr-FR')} km</strong> (le km de départ ne peut pas être inférieur)
            </div>
          )}
        </div>
      ) : (
        <div className="form-section">
          <h3>Véhicule loué (partenaire)</h3>
          <div className="form-row">
            <div>
              <label>Loueur (partenaire)</label>
              <select value={formPret.loueur_id} onChange={e => setP({ loueur_id: e.target.value })}>
                <option value="">— Sélectionner un loueur —</option>
                {loueurs.map(l => <option key={l.id} value={l.id}>{l.nom}</option>)}
              </select>
              {onOpenAjouterLoueur && (
                <div style={{ marginTop: 4 }}>
                  <button type="button" className="vc-history-toggle" onClick={onOpenAjouterLoueur}>+ Nouveau loueur</button>
                </div>
              )}
            </div>
            <div>
              <label>N° de contrat loueur</label>
              <input type="text" placeholder="ex : HZ-2026-001" value={formPret.loueur_num_contrat}
                onChange={e => setP({ loueur_num_contrat: e.target.value })} />
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Immatriculation du véhicule loué</label>
              <input type="text" placeholder="AB123CD" value={formPret.loueur_immat}
                style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                onChange={e => setP({ loueur_immat: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} />
            </div>
            <div>
              <label>Marque / modèle du véhicule loué</label>
              <input type="text" placeholder="ex : Peugeot 208" value={formPret.loueur_marque}
                onChange={e => setP({ loueur_marque: e.target.value })} />
            </div>
          </div>
        </div>
      )}

      <PermisDropzone
        disabled={saving}
        onFilePicked={(file) => addPretFichierBuffer(null, file)}
        onExtracted={(champs) => {
          setP({
            ...(champs.nom ? { client_nom: champs.nom.toUpperCase() } : {}),
            ...(champs.prenom ? { client_prenom: champs.prenom } : {}),
            ...(champs.date_naissance ? { client_date_naissance: champs.date_naissance } : {}),
            ...(champs.lieu_naissance ? { client_lieu_naissance: champs.lieu_naissance } : {}),
            ...(champs.numero_permis ? { client_permis_numero: champs.numero_permis } : {}),
            ...(champs.date_obtention ? { client_permis_date_obtention: champs.date_obtention } : {}),
          });
        }}
      />

      <div className="form-section">
        <h3>Client emprunteur</h3>
        <div className="form-row">
          <input type="text" placeholder="NOM *" value={formPret.client_nom}
            onChange={e => setP({ client_nom: e.target.value.toUpperCase() })} required />
          <input type="text" placeholder="Prénom" value={formPret.client_prenom}
            onChange={e => setP({ client_prenom: e.target.value })} />
        </div>
        <div className="form-row">
          <input type="tel" placeholder="Téléphone" value={formPret.client_telephone}
            onChange={e => setP({ client_telephone: e.target.value })} />
          <input type="email" placeholder="Email" value={formPret.client_email}
            onChange={e => setP({ client_email: e.target.value })} />
        </div>
        <div className="form-row">
          <div>
            <label>Date de naissance</label>
            <input type="date" value={formPret.client_date_naissance}
              onChange={e => setP({ client_date_naissance: e.target.value })} />
          </div>
          <div>
            <label>Lieu de naissance</label>
            <input type="text" placeholder="ex : Fort-de-France" value={formPret.client_lieu_naissance}
              onChange={e => setP({ client_lieu_naissance: e.target.value })} />
          </div>
        </div>
        <div className="form-row">
          <div>
            <label>N° de permis de conduire</label>
            <input type="text" value={formPret.client_permis_numero}
              onChange={e => setP({ client_permis_numero: e.target.value })} />
          </div>
          <div>
            <label>Date d'obtention du permis</label>
            <input type="date" value={formPret.client_permis_date_obtention}
              onChange={e => setP({ client_permis_date_obtention: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Dates & Détails</h3>
        <div className="form-row">
          <div><label>Date début *</label><input type="date" value={formPret.date_debut} onChange={e => setP({ date_debut: e.target.value })} required /></div>
          <div><label>Date fin prévue *</label><input type="date" value={formPret.date_fin_prevue} onChange={e => setP({ date_fin_prevue: e.target.value })} required /></div>
        </div>
        <div className="form-row">
          {!isLocation && (
            <div>
              <label>KM départ *</label>
              <input type="number" placeholder="ex : 25000" value={formPret.km_depart}
                onChange={e => setP({ km_depart: e.target.value })} required />
            </div>
          )}
          <div>
            <label>Motif</label>
            <select value={formPret.motif} onChange={e => setP({ motif: e.target.value })}>
              {['Révision', 'Réparation', 'Carrosserie', 'Expertise', 'Autre'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div>
            <label>Immatriculation véhicule client</label>
            <input type="text" placeholder="AB123CD" value={formPret.immat_client}
              style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
              onChange={e => setP({ immat_client: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} />
          </div>
          <div>
            <label>N° OR client</label>
            <input type="text" placeholder="ex : 12345" value={formPret.or_numero}
              onChange={e => setP({ or_numero: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Caution</h3>
        <div className="form-row">
          <div>
            <label>Type</label>
            <select value={formPret.caution_type}
              onChange={e => setP({ caution_type: e.target.value, caution_cheque_numero: e.target.value === 'cheque' ? formPret.caution_cheque_numero : '' })}>
              <option value="cheque">Chèque</option>
              <option value="cb">Empreinte CB</option>
            </select>
          </div>
          <div>
            <label>Montant (€)</label>
            <input type="number" min="0" step="0.01" placeholder="ex : 500" value={formPret.caution_montant}
              onChange={e => setP({ caution_montant: e.target.value })} />
          </div>
        </div>
        {formPret.caution_type === 'cheque' && (
          <div className="form-row">
            <div>
              <label>N° de chèque</label>
              <input type="text" placeholder="ex : 0000123" value={formPret.caution_cheque_numero}
                onChange={e => setP({ caution_cheque_numero: e.target.value })} />
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Facturation</h3>
        <div className="form-row">
          <div>
            <label>Coût journalier (€ HT)</label>
            <input type="number" min="0" step="0.01" placeholder="ex : 35" value={formPret.cout_journalier}
              onChange={e => setP({ cout_journalier: e.target.value })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
            {fact ? (
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                <div>{fact.jours} jour{fact.jours > 1 ? 's' : ''} · HT <strong>{fmtE(fact.ht)} €</strong></div>
                <div>TVA 8,5 % <strong>{fmtE(fact.tva)} €</strong> · TTC <strong style={{ color: 'var(--color-accent)' }}>{fmtE(fact.ttc)} €</strong></div>
              </div>
            ) : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Renseigner le coût journalier pour le récap HT/TVA/TTC</span>}
          </div>
        </div>
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Facturation interne (société / autorisation)</summary>
          <div className="form-row" style={{ marginTop: 8 }}>
            <div><label>Société facturée</label><input type="text" value={formPret.societe} onChange={e => setP({ societe: e.target.value })} /></div>
            <div><label>Autorisé par</label><input type="text" value={formPret.autorise_par} onChange={e => setP({ autorise_par: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div><label>N° bon de commande</label><input type="text" value={formPret.num_bon_commande} onChange={e => setP({ num_bon_commande: e.target.value })} /></div>
          </div>
        </details>
      </div>

      <div className="form-section">
        <h3>Pièces jointes (permis, CNI…)</h3>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>
          Glissez-déposez ou ajoutez les documents ici : ils seront enregistrés automatiquement à la création du prêt.
        </p>
        <CourtoisieDocsGrid
          fichiers={pretFichiersBuffer}
          pretId={null}
          onUpload={addPretFichierBuffer}
          onDelete={removePretFichierBuffer}
          uploading={false}
        />
      </div>
    </>
  );
}
